import { useQuery } from "@tanstack/react-query";
import { Button, ConfigProvider, Flex, Table, TableProps, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageHeading from "components/PageHeading.component";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { th, tnum } from "shared/components/TableText";
import { formatSignedCurrency } from "shared/utils";
import { localTzDate } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { PayrollRunStatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { PayrollFrequency } from "shared/types/enum";
import { PayrollRunListItem } from "shared/types";
import { payrollRunsOptions } from "hooks/queries/payroll";

const getFrequencyLabels = (): Record<PayrollFrequency, string> => ({
  [PayrollFrequency.Weekly]: translate("payrollPage.settings.frequency_weekly"),
  [PayrollFrequency.TwiceMonthly]: translate("payrollPage.settings.frequency_twice_monthly"),
  [PayrollFrequency.Monthly]: translate("payrollPage.settings.frequency_monthly"),
});

const PayrollHistoryPage = () => {
  const navigate = useNavigate();
  const { data: runs = [], isLoading } = useQuery(payrollRunsOptions());

  const columns: TableProps<PayrollRunListItem>["columns"] = [
    {
      title: th(translate("payrollPage.history.col_period")),
      key: "period",
      render: (_, run) => (
        <Typography.Text style={{ fontWeight: 600 }}>
          {localTzDate(run.period_start).format("D MMM")} - {localTzDate(run.period_end).format("D MMM YYYY")}
        </Typography.Text>
      ),
    },
    {
      title: th(translate("payrollPage.history.col_frequency")),
      dataIndex: "frequency",
      render: (f: PayrollFrequency) => getFrequencyLabels()[f],
    },
    { title: th(translate("payrollPage.history.col_staff_count")), dataIndex: "staff_count", render: (v: number) => <span style={tnum}>{v}</span> },
    {
      title: th(translate("payrollPage.history.col_net_pay")),
      dataIndex: "total_net_pay",
      render: (v: number) => <span style={tnum}>{formatSignedCurrency(v)}</span>,
    },
    {
      title: th(translate("payrollPage.history.col_status")),
      dataIndex: "status",
      render: (status) => <PayrollRunStatusBadge status={status} />,
    },
    {
      title: th(translate("payrollPage.history.col_generated")),
      dataIndex: "generated_at",
      render: (v: Date) => localTzDate(v).format("DD MMM YYYY"),
    },
    {
      title: th(translate("payrollPage.history.col_finalized")),
      dataIndex: "finalized_at",
      render: (v: Date | null) => (v ? localTzDate(v).format("DD MMM YYYY") : "-"),
    },
  ];

  return (
    <>
      <PageHeading
        title={
          // Muted breadcrumb back-link on its own row, same pattern as
          // ClassCommissionPage.tsx — this page is a spoke reached from the
          // Pengaturan Penggajian hub (via its own "Riwayat Penggajian"
          // button), so it needs a way back there.
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(PATHS.PAYROLL)}
            style={{ paddingLeft: 0, color: colors.ink2, fontWeight: 400 }}
          >
            {translate("payrollPage.rates.back_to_settings")}
          </Button>
        }
      />
      <Flex align="center" justify="space-between" gap={16} wrap="wrap" style={{ marginBottom: 20 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {translate("payrollPage.history.title")}
        </Typography.Title>
        <ConfigProvider theme={modalButtonsTheme}>
          <Button type="primary" onClick={() => navigate(PATHS.PAYROLL_RUN_NEW, { state: { from: PATHS.PAYROLL_HISTORY } })}>
            {translate("payrollPage.history.new_run_button")}
          </Button>
        </ConfigProvider>
      </Flex>
      <Table<PayrollRunListItem>
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={runs}
        onRow={(record) => ({ onClick: () => navigate(`${PATHS.PAYROLL_RUN}/${record.id}`), style: { cursor: "pointer" } })}
        pagination={false}
        // Even column widths, same as the Gaji Pokok and Item Berulang tables:
        // sizing to content left Periode and Jumlah Karyawan wider than they
        // need and the rest cramped.
        tableLayout="fixed"
      />
    </>
  );
};

export default PayrollHistoryPage;
